import React, { useEffect } from 'react';
import { Card, Row, Col, Button, Typography, Space, Divider } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from '../../stores/root-store-context';
import { observer } from 'mobx-react-lite';
import UkrainiansSaleBanner from '../banners/UkrainiansSaleBanner';

const { Title, Text, Paragraph } = Typography;

interface Feature {
    text: string;
    active: boolean;
    subFeatures?: string[];
}

interface TariffsProps {
    currentTariff?: string;
    currentIntervalType?: "Monthly" | "Yearly";
    callBackSaving?: (tariffId: number, intervalType: "Monthly" | "Yearly") => void;
    discount?: number;
}

export const Tariffs: React.FC<TariffsProps> = observer((props) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { applicationStore, tariffPlansStore } = useStore();

    useEffect(() => {
        tariffPlansStore.getTariffPlans();
    }, [tariffPlansStore]);

    const formatPriceWithDiscount = (priceStr: string, discount?: number) => {
        if (!discount || discount <= 0) return { original: priceStr, discounted: undefined };

        const currencySymbol = priceStr.trim()[0]; // "$" або "₴"
        const priceValue = parseFloat(priceStr.replace(/[^0-9.]/g, "")) || 0;
        const discountedValue = (priceValue * (1 - discount));

        return {
            original: priceStr,
            discounted: `${currencySymbol}${discountedValue.toFixed(2)}`
        };
    };

    const plans = [
        {
            id: 1,
            title: t('Tariffs.Basic.Title'),
            tariffParam: 'basic',
            subtitle: t('Tariffs.Basic.Subtitle'),
            price: `$${tariffPlansStore.globalPricing.basic.month}`,
            altPrice: `₴${tariffPlansStore.localPricing.basic.month}`,
            yearlyPrice: `$${tariffPlansStore.globalPricing.basic.year}`,
            altYearlyPrice: `₴${tariffPlansStore.localPricing.basic.year}`,
            period: t('Tariffs.Monthly'),
            yearlyPeriod: t('Tariffs.Yearly'),
            features: [
                { text: t('Tariffs.Features.ProjectManagement'), active: true },
                { text: t('Tariffs.Features.SalaryManagement'), active: true },
                { text: t('Tariffs.Features.VacationManagement'), active: true },
                { text: t('Tariffs.Features.TimeTracking'), active: true },
                { text: t('Tariffs.Features.EmployeeTraining'), active: false },
                { text: t('Tariffs.Features.AITesting'), active: false },
                { text: t('Tariffs.Features.SkillDevelopment'), active: false },
                { text: t('Tariffs.Features.CareerPlanning'), active: false },
                { text: t('Tariffs.Features.AdvancedAnalytics'), active: false },
            ] as Feature[],
            description: t('Tariffs.Basic.Description'),
            color: '#8B5CF6',
            borderColor: '#EDE9FE',
            //savings: 'Pay yearly & save ~20%'
        },
        {
            id: 6,
            title: t('Tariffs.Comfort.Title'),
            tariffParam: 'comfort',
            subtitle: t('Tariffs.Comfort.Subtitle'),
            price: `$${tariffPlansStore.globalPricing.comfort.month}`,
            yearlyPrice: `$${tariffPlansStore.globalPricing.comfort.year}`,
            altPrice: `₴${tariffPlansStore.localPricing.comfort.month}`,
            altYearlyPrice: `₴${tariffPlansStore.localPricing.comfort.year}`,
            period: t('Tariffs.Monthly'),
            yearlyPeriod: t('Tariffs.Yearly'),
            features: [
                { text: t('Tariffs.Features.ProjectManagement'), active: true },
                { text: t('Tariffs.Features.SalaryManagement'), active: true },
                { text: t('Tariffs.Features.VacationManagement'), active: true },
                { text: t('Tariffs.Features.TimeTracking'), active: true },
                { text: t('Tariffs.Features.EmployeeTraining'), active: true },
                { text: t('Tariffs.Features.AITesting'), active: true },
                { text: t('Tariffs.Features.SkillDevelopment'), active: true },
                { text: t('Tariffs.Features.CareerPlanning'), active: true },
                { text: t('Tariffs.Features.AdvancedAnalytics'), active: true },
                {
                    text: t('Tariffs.Features.KanbanTool.Title'),
                    active: true,
                    subFeatures: [
                        t('Tariffs.Features.KanbanTool.SubFeatures.Visualization'),
                        t('Tariffs.Features.KanbanTool.SubFeatures.TaskManagement'),
                        t('Tariffs.Features.KanbanTool.SubFeatures.Flexibility'),
                        t('Tariffs.Features.KanbanTool.SubFeatures.TimeManagement')
                    ]
                },
            ] as Feature[],
            description: t('Tariffs.Comfort.Description'),
            color: '#2563EB',
            borderColor: '#DBEAFE',
        },

        {
            id: 7,
            title: t('Tariffs.Infinity.Title'),
            tariffParam: 'infinity',
            subtitle: t('Tariffs.Infinity.Subtitle'),
            price: `$${tariffPlansStore.globalPricing.infinity.month}`,
            yearlyPrice: `$${tariffPlansStore.globalPricing.infinity.year}`,
            altPrice: `₴${tariffPlansStore.localPricing.infinity.month}`,
            altYearlyPrice: `₴${tariffPlansStore.localPricing.infinity.year}`,
            period: t('Tariffs.Monthly'),
            yearlyPeriod: t('Tariffs.Yearly'),
            features: [
                { text: t('Tariffs.Features.ProjectManagement'), active: true },
                { text: t('Tariffs.Features.SalaryManagement'), active: true },
                { text: t('Tariffs.Features.VacationManagement'), active: true },
                { text: t('Tariffs.Features.TimeTracking'), active: true },
                { text: t('Tariffs.Features.EmployeeTraining'), active: true },
                { text: t('Tariffs.Features.AITesting'), active: true },
                { text: t('Tariffs.Features.SkillDevelopment'), active: true },
                { text: t('Tariffs.Features.CareerPlanning'), active: true },
                { text: t('Tariffs.Features.AdvancedAnalytics'), active: true },
                {
                    text: t('Tariffs.Features.KanbanTool.Title'),
                    active: true,
                    subFeatures: [
                        t('Tariffs.Features.KanbanTool.SubFeatures.Visualization'),
                        t('Tariffs.Features.KanbanTool.SubFeatures.TaskManagement'),
                        t('Tariffs.Features.KanbanTool.SubFeatures.Flexibility'),
                        t('Tariffs.Features.KanbanTool.SubFeatures.TimeManagement')
                    ]
                },
                { text: t('Tariffs.Features.CRM'), active: true },
                { text: t('Tariffs.Features.ERP'), active: true },
            ] as Feature[],
            description: t('Tariffs.Infinity.Description'),
            color: '#059669',
            borderColor: '#D1FAE5',
            //savings: 'Pay yearly & save 20%'

        }
    ];

    const PriceInline: React.FC<{ priceStr: string; period: string; color: string }> = ({ priceStr, period, color }) => {
        const { original, discounted } = formatPriceWithDiscount(priceStr, props.discount);

        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "baseline",
                gap: 10,
                flexWrap: "wrap"
            }}>
                {discounted ? (
                    <>
                        <Text delete style={{ fontSize: "1.6rem", color: "#9CA3AF" }}>{original}</Text>
                        <Text style={{ fontSize: "2rem", color }}>{discounted}</Text>
                    </>
                ) : (
                    <Text style={{ fontSize: "2.8rem", color }}>{original}</Text>
                )}
                <Text className="text-gray-500" style={{ fontSize: "1.5rem" }}>{period}</Text>
            </div>
        );
    };

    return (
        <section className="py-48" style={{ backgroundColor: '#FAFAFA' }}>
            <UkrainiansSaleBanner />
            <br />
            <div className="container mx-auto" style={{
                maxWidth: '1800px',
                paddingLeft: '24px',
                paddingRight: '24px'
            }}>
                <Row gutter={[32, 48]} justify="center" align="stretch">
                    {plans.map((plan, index) => (
                        <Col key={index} xs={24} sm={24} md={8}>
                            <Card
                                bordered={true}
                                className="h-full tariff-card"
                                style={{
                                    borderColor: plan.borderColor,
                                    borderWidth: '2px',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    padding: index === 0 ? '22px' :
                                        index === 1 ? '24px' :
                                            index === 2 ? '26px' : '28px',
                                    transition: 'all 0.3s ease',
                                    ...(index === 0 ? {
                                        maxWidth: '540px',
                                        margin: '0 auto',
                                        transformOrigin: 'center center',
                                        height: 'auto'
                                    } : index === 1 ? {
                                        maxWidth: '540px',
                                        margin: '0 auto',
                                        transformOrigin: 'center center',
                                        height: 'auto'
                                    } : {
                                        maxWidth: '540px',
                                        margin: '0 auto',                                       
                                        height: 'auto'
                                    })
                                }}
                                onMouseEnter={(e) => {
                                    const target = e.currentTarget;
                                    target.style.borderColor = plan.color;
                                    target.style.boxShadow = `0 0 20px ${plan.color}33`;
                                }}
                                onMouseLeave={(e) => {
                                    const target = e.currentTarget;
                                    target.style.borderColor = plan.borderColor;
                                    target.style.boxShadow = 'none';
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    ...(index === 0 ? {} : { height: '100%' }),
                                    justifyContent: 'space-between'
                                }}>
                                    <div>
                                        <div className="text-center" style={{
                                            marginBottom: index === 0 ? '16px' :
                                                index === 1 ? '20px' :
                                                    index === 2 ? '24px' : '28px'
                                        }}>
                                            <Text className="text-6xl font-bold" style={{
                                                color: plan.color,
                                                fontSize: '1.9rem',
                                            }}>
                                                {t('Tariffs.UnlimitedUsers')}
                                            </Text>
                                            <Title level={2} style={{
                                                marginBottom: '16px',
                                                fontSize: index === 0 ? '1.8rem' :
                                                    index === 1 ? '1.9rem' :
                                                        index === 2 ? '2.0rem' : '2.1rem',
                                                fontWeight: 'bold'
                                            }}>
                                                {plan.title}
                                            </Title>
                                            <Text className="text-gray-600" style={{
                                                fontSize: index === 0 ? '1.2rem' :
                                                    index === 1 ? '1.2rem' :
                                                        index === 2 ? '1.2rem' : '1.2rem',
                                                display: 'block',
                                                marginBottom: index === 0 ? '0px' :
                                                    index === 1 ? '0px' :
                                                        index === 2 ? '20px' : '0px'
                                            }}>
                                                {plan.subtitle}
                                            </Text>

                                            <div className="my-8">
                                                <div className="mb-2">
                                                    <PriceInline priceStr={plan.price} period={plan.period} color={plan.color} />

                                                    {applicationStore.isUaCompany && (
                                                        <>
                                                            {applicationStore.isUaCompany && (
                                                                <>
                                                                    <Divider>{t("or")}</Divider>

                                                                    <PriceInline priceStr={plan.altPrice} period={plan.period} color={plan.color} />
                                                                </>
                                                            )}
                                                        </>
                                                    )}

                                                </div>
                                                <Text className="text-6xl font-bold" style={{
                                                    color: plan.color,
                                                    fontSize: '0.8rem',
                                                }}>
                                                    {t("Tariffs.planDescription")}
                                                </Text>
                                                {/* {plan.savings && (
                                                    <div className="">
                                                        <Text style={{ 
                                                            color: plan.color,
                                                            fontSize: '1.1rem',
                                                            fontWeight: 500,
                                                        }}>
                                                            {plan.savings}
                                                        </Text>
                                                    </div>
                                                )} */}
                                            </div>
                                        </div>

                                        <div style={{
                                            marginBottom: index === 0 ? '16px' :
                                                index === 1 ? '20px' :
                                                    index === 2 ? '20px' :
                                                        '28px'
                                        }}>
                                            {plan.features.map((feature, idx) => (
                                                <div key={idx}>
                                                    <div className="flex items-center" style={{
                                                        marginBottom: feature.subFeatures ? '8px' : '5px'
                                                    }}>
                                                        <CheckOutlined style={{
                                                            color: feature.active ? plan.color : '#9CA3AF',
                                                            opacity: feature.active ? 1 : 0.5
                                                        }} className="mr-3" />
                                                        <Text style={{
                                                            fontSize: index === 0 ? '13px' :
                                                                index === 1 ? '13px' :
                                                                    index === 2 ? '14px' : '14px',
                                                            color: feature.active ? 'inherit' : '#9CA3AF'
                                                        }}>
                                                            {feature.text}
                                                        </Text>
                                                    </div>
                                                    {feature.subFeatures && (
                                                        <div style={{ marginLeft: '28px', marginBottom: '8px' }}>
                                                            {feature.subFeatures.map((subFeature, subIdx) => (
                                                                <div key={subIdx} className="flex items-center" style={{
                                                                    marginBottom: '4px',
                                                                    opacity: 0.85,
                                                                    whiteSpace: 'nowrap',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    <div style={{
                                                                        width: '4px',
                                                                        height: '4px',
                                                                        backgroundColor: plan.color,
                                                                        borderRadius: '50%',
                                                                        marginRight: '8px',
                                                                        flexShrink: 0
                                                                    }} />
                                                                    <Text style={{
                                                                        fontSize: '13px',
                                                                        color: 'inherit',
                                                                        whiteSpace: 'normal'
                                                                    }}>
                                                                        {subFeature}
                                                                    </Text>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <Paragraph
                                            className="text-gray-600 text-center"
                                            style={{
                                                fontSize: index === 0 ? '14px' :
                                                    index === 1 ? '15px' :
                                                        index === 2 ? '15px' : '15px',
                                                marginBottom: index === 0 ? '16px' :
                                                    index === 1 ? '16px' :
                                                        index === 2 ? '16px' : '24px',
                                                marginLeft: '-8px',
                                                marginRight: '-8px',
                                                width: 'calc(100% + 16px)'
                                            }}
                                        >
                                            {plan.description}
                                        </Paragraph>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {(() => {
                                                const { discounted: discountedMonthly } = formatPriceWithDiscount(plan.price, props.discount);
                                                const { discounted: discountedYearly } = formatPriceWithDiscount(plan.yearlyPrice, props.discount);
                                                return (
                                                    <>
                                                        <Button
                                                            disabled={props.currentTariff === plan.id.toString() && props.currentIntervalType === "Monthly"}
                                                            type="primary"
                                                            size="large"
                                                            block
                                                            onClick={() => {
                                                                if (props.callBackSaving) {
                                                                    props.callBackSaving(plan.id, "Monthly");
                                                                } else {
                                                                    navigate(`/register-company?tariff=${plan.tariffParam}&period=month`);
                                                                }
                                                            }}
                                                            style={{
                                                                backgroundColor:
                                                                    props.currentTariff === plan.id.toString() && props.currentIntervalType === "Monthly"
                                                                        ? "gray"
                                                                        : plan.color,
                                                                borderColor: plan.color,
                                                                height: index === 0 ? '42px' :
                                                                    index === 1 ? '44px' :
                                                                        index === 2 ? '46px' : '48px',
                                                                fontSize: index === 0 ? '14px' :
                                                                    index === 1 ? '14px' :
                                                                        index === 2 ? '15px' : '16px',
                                                            }}
                                                        >
                                                            {t('Tariffs.MonthlyPlan')}{' '}
                                                            {discountedMonthly
                                                                ? (
                                                                    <>
                                                                        <span style={{ textDecoration: 'line-through', opacity: 0.6, marginRight: 4 }}>
                                                                            {plan.price}
                                                                        </span>
                                                                        {discountedMonthly}
                                                                    </>
                                                                )
                                                                : plan.price
                                                            }/{t('Tariffs.Month')}
                                                        </Button>

                                                        <Button
                                                            disabled={props.currentTariff === plan.id.toString() && props.currentIntervalType === "Yearly"}
                                                            type="primary"
                                                            size="large"
                                                            block
                                                            onClick={() => {
                                                                if (props.callBackSaving) {
                                                                    props.callBackSaving(plan.id, "Yearly");
                                                                } else {
                                                                    navigate(`/register-company?tariff=${plan.tariffParam}&period=year`);
                                                                }
                                                            }}
                                                            style={{
                                                                backgroundColor:
                                                                    props.currentTariff === plan.id.toString() && props.currentIntervalType === "Yearly"
                                                                        ? "gray"
                                                                        : 'white',
                                                                borderColor: plan.color,
                                                                color: plan.color,
                                                                height: index === 0 ? '42px' :
                                                                    index === 1 ? '44px' :
                                                                        index === 2 ? '46px' : '48px',
                                                                fontSize: index === 0 ? '14px' :
                                                                    index === 1 ? '14px' :
                                                                        index === 2 ? '15px' : '16px',
                                                            }}
                                                        >
                                                            {t('Tariffs.YearlyPlan')}{' '}
                                                            {discountedYearly
                                                                ? (
                                                                    <>
                                                                        <span style={{ textDecoration: 'line-through', opacity: 0.6, marginRight: 4 }}>
                                                                            {plan.yearlyPrice}
                                                                        </span>
                                                                        {discountedYearly}
                                                                    </>
                                                                )
                                                                : plan.yearlyPrice
                                                            }/{t('Tariffs.Year')}
                                                        </Button>
                                                    </>
                                                );
                                            })()}
                                        </div>

                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
            <br />
        </section>
    );
});

export default Tariffs; 